// IBAN country-specific lengths (ISO 13616)
const IBAN_LENGTHS: Record<string, number> = {
  AL: 28, AD: 24, AT: 20, AZ: 28, BH: 22, BY: 28, BE: 16, BA: 20,
  BR: 29, BG: 22, CR: 22, HR: 21, CY: 28, CZ: 24, DK: 18, DO: 28,
  EG: 29, SV: 28, EE: 20, FO: 18, FI: 18, FR: 27, GE: 22, DE: 22,
  GI: 23, GR: 27, GL: 18, GT: 28, HU: 28, IS: 26, IQ: 23, IE: 22,
  IL: 23, IT: 27, JO: 30, KZ: 20, XK: 20, KW: 30, LV: 21, LB: 28,
  LI: 21, LT: 20, LU: 20, MT: 31, MR: 27, MU: 30, MD: 24, MC: 27,
  ME: 22, NL: 18, MK: 19, NO: 15, PK: 24, PS: 29, PL: 28, PT: 25,
  QA: 29, RO: 24, LC: 32, SM: 27, SA: 24, RS: 22, SC: 31, SK: 24,
  SI: 19, ES: 24, SE: 24, CH: 21, TL: 23, TN: 24, TR: 26, UA: 29,
  AE: 23, GB: 22, VA: 22, VG: 24,
};

/**
 * Validate an IBAN string.
 * Returns null if valid, or an error message string if invalid.
 */
export const validateIBAN = (iban: string): string | null => {
  if (!iban) return null; // empty is handled by required-field validation

  // Remove spaces and convert to uppercase
  const cleaned = iban.replace(/\s/g, "").toUpperCase();

  // Must be alphanumeric
  if (!/^[A-Z0-9]+$/.test(cleaned)) {
    return "IBAN must contain only letters and digits";
  }

  // Minimum length check
  if (cleaned.length < 5) {
    return "IBAN is too short";
  }

  // Country code must be two letters
  const countryCode = cleaned.substring(0, 2);
  if (!/^[A-Z]{2}$/.test(countryCode)) {
    return "IBAN must start with a two-letter country code";
  }

  // Check digits must be two digits
  const checkDigits = cleaned.substring(2, 4);
  if (!/^[0-9]{2}$/.test(checkDigits)) {
    return "IBAN check digits (positions 3-4) must be numeric";
  }

  // Country-specific length check
  const expectedLength = IBAN_LENGTHS[countryCode];
  if (expectedLength && cleaned.length !== expectedLength) {
    return `IBAN for ${countryCode} must be ${expectedLength} characters (got ${cleaned.length})`;
  }

  // MOD 97 validation (ISO 7064)
  if (!mod97Check(cleaned)) {
    return "IBAN check digits are invalid";
  }

  return null;
};

/**
 * MOD 97 check per ISO 13616.
 * Move first 4 chars to end, convert letters to numbers (A=10..Z=35),
 * compute mod 97 — result must be 1.
 */
const mod97Check = (iban: string): boolean => {
  const rearranged = iban.substring(4) + iban.substring(0, 4);

  const numericStr = rearranged
    .split("")
    .map((ch) => {
      const code = ch.charCodeAt(0);
      // A=65 -> 10, B=66 -> 11, ..., Z=90 -> 35
      return code >= 65 && code <= 90 ? (code - 55).toString() : ch;
    })
    .join("");

  // Process in chunks to avoid BigInt for broad compatibility
  let remainder = 0;
  for (let i = 0; i < numericStr.length; i++) {
    remainder = (remainder * 10 + parseInt(numericStr[i], 10)) % 97;
  }

  return remainder === 1;
};
